import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/contexts/AuthContext";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { playMessageSent, playMessageReceived } from "@/lib/sounds";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Send, MessageCircle, Swords, PenSquare, Search, Loader2, ArrowLeft, UserRound } from "lucide-react";
import { useLocation } from "wouter";
import { StudentEmptyState } from "@/components/StudentEmptyState";
import { Initials } from "@/components/profile/ProfileSections";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ChallengeDialog } from "@/components/ChallengeDialog";
import { ChallengeInviteCard } from "@/components/ChallengeInviteCard";
import { getDisplayName } from "@/lib/userDisplay";
import { OnlineDot } from "@/components/OnlineDot";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { StaffBadge } from "@/components/StaffBadge";
import giftmeLogo from "@assets/1000184394_1759430460517.png";

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  recipientId: string;
  content: string;
  isRead: boolean;
  createdAt: string;
}

interface Conversation {
  id: string;
  user1Id: string;
  user2Id: string;
  lastMessageAt: string;
  otherUser: {
    id: string;
    firstName: string;
    lastName: string;
    username: string;
    profilePhotoUrl?: string;
    role?: string;
  };
  lastMessage?: Message;
  unreadCount: number;
}

interface Challenge {
  id: string;
  creatorId: string;
  opponentId: string;
  mode: 'live' | 'async';
  subject: string;
  topic?: string;
  questionCount: number;
  status: 'pending' | 'accepted' | 'declined' | 'in_progress' | 'completed';
  createdAt: string;
}

export default function Messages() {
  const { user } = useAuth();
  const { ws, subscribe } = useWebSocket();
  const { toast } = useToast();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  const [friendSearch, setFriendSearch] = useState("");
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingSent = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const hasAttemptedRefetch = useRef<string | null>(null);

  // Get conversation ID from URL query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const conversationParam = urlParams.get('conversation');

  // Fetch conversations
  const [conversationSearch, setConversationSearch] = useState("");
  const [, navigate] = useLocation();

  const { data: conversations = [], isLoading: conversationsLoading } = useQuery<Conversation[]>({
    queryKey: ["/api/messages/conversations"],
  });

  const otherUserIds = conversations.map(c => c.otherUser.id);
  const { isOnline } = useOnlineStatus(otherUserIds);

  // Fetch friends for new conversation dialog
  const { data: following = [] } = useQuery<any[]>({
    queryKey: ["/api/social/following"],
    enabled: newMessageOpen,
  });

  // Auto-select conversation from URL parameter
  useEffect(() => {
    if (conversationParam && !selectedConversationId) {
      const conversation = conversations.find(c => c.id === conversationParam);
      if (conversation) {
        setSelectedConversationId(conversationParam);
        hasAttemptedRefetch.current = null;
        // Clean up URL
        window.history.replaceState({}, '', '/messages');
      } else if (!conversationsLoading && hasAttemptedRefetch.current !== conversationParam) {
        // Conversation not found in list - might be newly created, refetch once
        hasAttemptedRefetch.current = conversationParam;
        queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
      }
    }
  }, [conversationParam, conversations, selectedConversationId, conversationsLoading]);

  // Get selected conversation
  const selectedConversation = conversations.find(c => c.id === selectedConversationId);
  const recipientId = selectedConversation?.otherUser.id;

  // Fetch messages for selected conversation
  const { data: messages = [], isLoading: messagesLoading } = useQuery<Message[]>({
    queryKey: ["/api/messages/conversation", selectedConversationId],
    enabled: !!selectedConversationId,
  });

  // Fetch challenges
  const { data: allChallenges = [] } = useQuery<Challenge[]>({
    queryKey: ["/api/challenges"],
    enabled: !!user,
  });

  // Filter challenges for current conversation
  // Show active + recently declined (declined within last 5 min for visual feedback)
  const conversationChallenges = allChallenges.filter(c =>
    selectedConversation && (
      (c.creatorId === user?.id && c.opponentId === selectedConversation.otherUser.id) ||
      (c.opponentId === user?.id && c.creatorId === selectedConversation.otherUser.id)
    ) && (
      c.status === 'pending' || c.status === 'accepted' || c.status === 'in_progress' || c.status === 'declined'
    )
  ).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  // Invalidate unread count when viewing a conversation
  useEffect(() => {
    if (selectedConversationId && messages.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/unread-count'] });
    }
  }, [selectedConversationId, messages.length]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!selectedConversationId || !recipientId) {
        throw new Error("No conversation selected");
      }
      
      const result = await apiRequest("POST", "/api/messages/send", {
        conversationId: selectedConversationId,
        recipientId,
        content
      });
      return result.json();
    },
    onSuccess: (newMessage) => {
      // Add message to cache optimistically
      queryClient.setQueryData(
        ["/api/messages/conversation", selectedConversationId],
        (old: Message[] = []) => [...old, newMessage]
      );

      // Update conversations list and unread count
      queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/unread-count'] });

      // Send via WebSocket for real-time delivery to recipient
      if (ws && ws.readyState === WebSocket.OPEN && recipientId) {
        ws.send(JSON.stringify({
          type: "message",
          recipientId,
          data: newMessage
        }));
      }

      playMessageSent();
      setMessageText("");
    },
    onError: () => {
      toast({
        title: "Failed to send message",
        variant: "destructive"
      });
    }
  });

  // Keep a ref to selectedConversationId for use inside stable subscribe callbacks
  const selectedConversationIdRef = useRef(selectedConversationId);
  useEffect(() => { selectedConversationIdRef.current = selectedConversationId; }, [selectedConversationId]);

  // Subscribe to incoming messages via global WebSocket
  useEffect(() => {
    return subscribe('new_message', (data) => {
      const newMessage = data.message;
      playMessageReceived();
      if (newMessage.conversationId === selectedConversationIdRef.current) {
        queryClient.setQueryData(
          ["/api/messages/conversation", selectedConversationIdRef.current],
          (old: Message[] = []) => [...old, newMessage]
        );
      }
      queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/unread-count'] });
    });
  }, [subscribe]);

  // Subscribe to typing indicators
  useEffect(() => {
    return subscribe('typing', (data) => {
      // Only show indicator if it's the person we're currently chatting with
      if (!selectedConversationIdRef.current) return;
      setIsOtherTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsOtherTyping(false), 3000);
    });
  }, [subscribe]);

  // Subscribe to challenge events
  useEffect(() => {
    const unsubs = [
      subscribe('challenge_invite', () => {
        queryClient.invalidateQueries({ queryKey: ["/api/challenges"] });
        queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
        toast({ title: "New Challenge!", description: "You have a new quiz challenge" });
      }),
      subscribe('challenge_accepted', () => {
        queryClient.invalidateQueries({ queryKey: ["/api/challenges"] });
        toast({ title: "Challenge Accepted!", description: "Your opponent has accepted the challenge" });
      }),
      subscribe('challenge_declined', () => {
        queryClient.invalidateQueries({ queryKey: ["/api/challenges"] });
        toast({ title: "Challenge Declined", description: "Your opponent declined the challenge" });
      }),
      subscribe('challenge_completed', () => {
        queryClient.invalidateQueries({ queryKey: ["/api/challenges"] });
        queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
        if (selectedConversationIdRef.current) {
          queryClient.invalidateQueries({ queryKey: ["/api/messages/conversation", selectedConversationIdRef.current] });
        }
        toast({ title: "Challenge Complete!", description: "Both players finished. Tap to see results!" });
      }),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe, toast]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = () => {
    const trimmed = messageText.trim();
    if (!trimmed) return;
    
    if (trimmed.length > 2000) {
      toast({
        title: "Message too long",
        description: "Messages must be under 2000 characters",
        variant: "destructive"
      });
      return;
    }
    
    sendMessageMutation.mutate(trimmed);
  };

  const startConversationMutation = useMutation({
    mutationFn: async (recipientId: string) => {
      const res = await apiRequest('POST', '/api/messages/conversation', { recipientId });
      return res.json();
    },
    onSuccess: (data) => {
      setNewMessageOpen(false);
      setFriendSearch("");
      queryClient.invalidateQueries({ queryKey: ["/api/messages/conversations"] });
      setSelectedConversationId(data.id);
    },
    onError: () => {
      toast({ title: "Error", description: "Could not start conversation", variant: "destructive" });
    }
  });

  const sendTypingIndicator = useCallback(() => {
    const now = Date.now();
    if (ws && ws.readyState === WebSocket.OPEN && selectedConversation && now - lastTypingSent.current > 2000) {
      lastTypingSent.current = now;
      ws.send(JSON.stringify({ type: "typing", recipientId: selectedConversation.otherUser.id }));
    }
  }, [ws, selectedConversation]);  // ws here is the global WS from context

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageText(e.target.value);
    sendTypingIndicator();
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <StudentEmptyState illustration="owl-lock" title="Sign in to see your messages." action={<Button onClick={() => navigate("/login")}>Sign in</Button>} />
      </div>
    );
  }

  // On mobile: show chat panel if a conversation is selected, else show list
  const showChat = !!selectedConversationId;
  const snippetOf = (content: string | undefined) => {
    if (!content) return "No messages yet";
    if (content.startsWith('Challenge:')) return "Quiz challenge";
    if (content.startsWith('Challenge accepted:')) return "Challenge accepted";
    return content.replace(/\s*\[challenge:[^\]]+\]/, '');
  };
  const visibleConversations = conversations.filter(c => {
    if (!conversationSearch.trim()) return true;
    return `${c.otherUser.firstName} ${c.otherUser.lastName}`.toLowerCase().includes(conversationSearch.toLowerCase());
  });
  const unreadTotal = conversations.reduce((a, c) => a + (c.unreadCount || 0), 0);

  return (
    // Full height minus top nav (4rem)
    <div className="md:container md:mx-auto md:p-4 h-[calc(100vh-4rem)]">
      <div className="flex md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-4 h-full">

        {/* Conversations */}
        <section className={`w-full md:col-span-1 flex-col md:rounded-2xl md:border border-border bg-card overflow-hidden ${showChat ? 'hidden md:flex' : 'flex'}`} data-testid="panel-conversations">
          <div className="p-4 border-b border-border space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold tracking-tight">Messages</h1>
                <p className="text-xs text-muted-foreground">{unreadTotal > 0 ? `${unreadTotal} unread` : `${conversations.length} ${conversations.length === 1 ? 'chat' : 'chats'}`}</p>
              </div>
              <Button size="icon" className="rounded-lg h-10 w-10" onClick={() => setNewMessageOpen(true)} title="New message" data-testid="button-new-message"><PenSquare className="h-4 w-4" /></Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search chats" value={conversationSearch} onChange={(e) => setConversationSearch(e.target.value)} className="pl-9 h-10 rounded-lg bg-muted/40" data-testid="input-search-conversations" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversationsLoading ? (
              <div className="p-6 text-center text-muted-foreground text-sm">Loading chats...</div>
            ) : visibleConversations.length === 0 ? (
              <div className="p-4">
                <StudentEmptyState illustration="owl-search" size={100}
                  title={conversations.length === 0 ? "No chats yet." : "No chats match that name."}
                  hint={conversations.length === 0 ? "Follow a classmate, then message them from their profile or the leaderboard." : undefined}
                  action={conversations.length === 0 ? <Button size="sm" onClick={() => navigate("/social")}>Find friends</Button> : undefined} />
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {visibleConversations.map((conversation) => {
                  const active = selectedConversationId === conversation.id;
                  const unread = conversation.unreadCount > 0;
                  return (
                    <li key={conversation.id}>
                      <button
                        data-testid={`conversation-${conversation.otherUser.id}`}
                        onClick={() => setSelectedConversationId(conversation.id)}
                        className={`w-full px-4 py-3 text-left transition-colors flex items-center gap-3 ${active ? 'bg-primary/10' : 'hover:bg-primary/5'}`}
                      >
                        <div className="relative shrink-0">
                          <Initials user={conversation.otherUser} className="h-11 w-11" />
                          <OnlineDot isOnline={isOnline(conversation.otherUser.id)} className="absolute bottom-0 right-0 w-3 h-3 border-2 border-card rounded-full" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`truncate flex items-center gap-1 ${unread ? 'font-bold' : 'font-semibold'}`}>
                              {conversation.otherUser.firstName} {conversation.otherUser.lastName}
                              {conversation.otherUser.role === 'admin' && <StaffBadge />}
                            </p>
                            <span className="text-[11px] text-muted-foreground shrink-0">{formatDistanceToNow(new Date(conversation.lastMessageAt), { addSuffix: false })}</span>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate ${unread ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {snippetOf(conversation.lastMessage?.content)}
                            </p>
                            {unread && <span className="shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center tabular-nums">{conversation.unreadCount}</span>}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* Chat */}
        <section className={`w-full md:col-span-2 flex-col md:rounded-2xl md:border border-border bg-card overflow-hidden ${!showChat ? 'hidden md:flex' : 'flex'}`} data-testid="panel-chat">
          {selectedConversation ? (
            <>
              <div className="border-b border-border px-3 md:px-4 py-3 flex items-center gap-3 shrink-0">
                <button className="md:hidden p-2 -ml-2 rounded-lg text-muted-foreground hover:bg-muted" onClick={() => setSelectedConversationId(null)} aria-label="Back to conversations"><ArrowLeft className="h-5 w-5" /></button>
                <button type="button" className="relative shrink-0" onClick={() => selectedConversation.otherUser.username !== 'edufocal_bot' && navigate(`/user/${selectedConversation.otherUser.id}`)} aria-label="View profile">
                  <Initials user={selectedConversation.otherUser} className="h-10 w-10" />
                  <OnlineDot isOnline={isOnline(selectedConversation.otherUser.id)} className="absolute bottom-0 right-0 w-3 h-3 border-2 border-card rounded-full" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold leading-tight flex items-center gap-1 truncate">
                    {selectedConversation.otherUser.firstName} {selectedConversation.otherUser.lastName}
                    {selectedConversation.otherUser.role === 'admin' && <StaffBadge />}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    {isOnline(selectedConversation.otherUser.id)
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300 font-medium"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />Online now</span>
                      : getDisplayName(selectedConversation.otherUser)}
                  </p>
                </div>
                {selectedConversation.otherUser.username !== 'edufocal_bot' && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button variant="outline" size="sm" className="rounded-lg hidden sm:inline-flex" onClick={() => navigate(`/user/${selectedConversation.otherUser.id}`)}><UserRound className="h-4 w-4 mr-1.5" />Profile</Button>
                    <Button size="sm" className="rounded-lg bg-rose-500 hover:bg-rose-600 text-white" onClick={() => setChallengeDialogOpen(true)} data-testid="button-challenge"><Swords className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Challenge</span></Button>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-2.5 bg-primary/5" ref={scrollAreaRef}>
                {messagesLoading ? (
                  <div className="text-center text-muted-foreground text-sm">Loading messages...</div>
                ) : messages.length === 0 && conversationChallenges.length === 0 ? (
                  <StudentEmptyState illustration="owl-wave" size={100} title={`Say hi to ${selectedConversation.otherUser.firstName}.`} hint="Your messages stay between the two of you." />
                ) : (
                  messages.map((message) => {
                    const isMine = message.senderId === user.id;
                    const isBot = selectedConversation?.otherUser.username === 'edufocal_bot' && !isMine;
                    const isPrizeMessage = isBot && /prize|won/i.test(message.content);
                    const challengeIdMatch = message.content.match(/\[challenge:([^\]]+)\]/);
                    const isChallengeMsg = message.content.startsWith('Challenge:') || message.content.startsWith('Challenge accepted:');
                    const when = formatDistanceToNow(new Date(message.createdAt), { addSuffix: true });

                    if (isChallengeMsg) {
                      let matchingChallenge: Challenge | undefined;
                      if (challengeIdMatch) matchingChallenge = conversationChallenges.find(c => c.id === challengeIdMatch[1]);
                      if (message.content.startsWith('Challenge accepted:')) return null;
                      if (matchingChallenge) {
                        return (
                          <div key={message.id} data-testid={`message-${message.id}`}>
                            <ChallengeInviteCard
                              challenge={matchingChallenge}
                              isCreator={matchingChallenge.creatorId === user?.id}
                              creatorName={matchingChallenge.creatorId === user?.id ? `${user.firstName} ${user.lastName}` : `${selectedConversation.otherUser.firstName} ${selectedConversation.otherUser.lastName}`}
                              opponentName={matchingChallenge.opponentId === user?.id ? `${user.firstName} ${user.lastName}` : `${selectedConversation.otherUser.firstName} ${selectedConversation.otherUser.lastName}`}
                            />
                          </div>
                        );
                      }
                      return (
                        <div key={message.id} data-testid={`message-${message.id}`} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                          <div className="max-w-[80%] rounded-2xl px-4 py-2.5 bg-rose-500/10 border border-rose-500/30">
                            <p className="text-sm flex items-center gap-1.5"><Swords className="w-4 h-4 text-rose-600 shrink-0" />{message.content.replace(/\s*\[challenge:[^\]]+\]/, '')}</p>
                            <p className="text-[11px] mt-1 text-muted-foreground">{when}</p>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={message.id} data-testid={`message-${message.id}`}>
                        <div className={`flex items-end gap-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
                          {!isMine && <Initials user={selectedConversation.otherUser} className="h-7 w-7 shrink-0 mb-4" />}
                          <div className={`max-w-[80%] md:max-w-[70%] px-4 py-2.5 shadow-sm ${isMine ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md' : 'bg-card border border-border rounded-2xl rounded-bl-md'}`}>
                            <p className="break-words whitespace-pre-wrap text-[15px] leading-snug">{message.content.replace(/\s*\[challenge:[^\]]+\]/, '')}</p>
                            <p className={`text-[11px] mt-1 ${isMine ? 'text-primary-foreground/70 text-right' : 'text-muted-foreground'}`}>{when}</p>
                          </div>
                        </div>
                        {isPrizeMessage && (
                          <div className="flex items-center gap-2 mt-1.5 ml-10">
                            <span className="text-xs text-muted-foreground">Powered by</span>
                            <img src={giftmeLogo} alt="Giftme" className="h-4" />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {isOtherTyping && (
                  <div className="flex justify-start items-end gap-2">
                    <Initials user={selectedConversation.otherUser} className="h-7 w-7 shrink-0" />
                    <div className="bg-card border border-border rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1">
                      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="border-t border-border p-3 shrink-0 bg-card">
                <div className="flex gap-2 items-center">
                  <Input
                    data-testid="input-message"
                    placeholder={`Message ${selectedConversation.otherUser.firstName}`}
                    value={messageText}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    disabled={sendMessageMutation.isPending}
                    className="flex-1 h-11 rounded-full px-4 bg-muted/40"
                  />
                  <Button data-testid="button-send-message" size="icon" onClick={handleSendMessage} disabled={!messageText.trim() || sendMessageMutation.isPending} className="shrink-0 h-11 w-11 rounded-full" aria-label="Send">
                    {sendMessageMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-6">
              <StudentEmptyState illustration="owl-wave" title="Pick a chat to start talking." hint="Or start a new one with the pen button." action={<Button onClick={() => setNewMessageOpen(true)}><PenSquare className="h-4 w-4 mr-2" />New message</Button>} />
            </div>
          )}
        </section>
      </div>

      {selectedConversation && user && (
        <ChallengeDialog
          open={challengeDialogOpen}
          onOpenChange={setChallengeDialogOpen}
          opponentId={selectedConversation.otherUser.id}
          opponentName={`${selectedConversation.otherUser.firstName} ${selectedConversation.otherUser.lastName}`}
          userGrade={user.grade}
        />
      )}

      {/* New Message Dialog */}
      <Dialog open={newMessageOpen} onOpenChange={setNewMessageOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search friends"
                value={friendSearch}
                onChange={(e) => setFriendSearch(e.target.value)}
                className="pl-9 h-11 rounded-lg"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto divide-y">
              {following.length === 0 ? (
                <StudentEmptyState illustration="owl-search" size={90} title="No friends to message yet." hint="Follow someone from the leaderboard or their profile first." />
              ) : (
                following
                  .filter((f: any) => {
                    if (!friendSearch.trim()) return true;
                    const name = `${f.firstName} ${f.lastName}`.toLowerCase();
                    return name.includes(friendSearch.toLowerCase());
                  })
                  .map((friend: any) => (
                    <button
                      key={friend.followingId}
                      onClick={() => startConversationMutation.mutate(friend.followingId)}
                      disabled={startConversationMutation.isPending}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left"
                    >
                      <Initials user={friend} className="h-10 w-10" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{friend.firstName} {friend.lastName}</p>
                        <p className="text-xs text-muted-foreground">Grade {friend.grade}</p>
                      </div>
                    </button>
                  ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
